import PossiblePennyBadge from "./PossiblePennyBadge";
import { useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Download, Search } from "lucide-react";
import StoreSearchSortControls from "./StoreSearchSortControls";
import type { StoreProductResult } from "../services/ProductSearchService";
import {
  downloadStoreSearchReport,
  filterStoreSearchResults,
  isStoreResultInStock,
} from "../utils/storeSearchExport";
import { sortStoreSearchResults, type StoreSearchSort } from "../utils/storeSearchSort";

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

interface VirtualizedStoreSearchListProps {
  results: StoreProductResult[];
  retailer: string;
  storeSort: StoreSearchSort;
  onSortChange: (sort: StoreSearchSort) => void;
  storeFilter: string;
  onStoreFilterChange: (value: string) => void;
  inStockOnly: boolean;
  onInStockOnlyChange: (value: boolean) => void;
  getInventoryLabel: (result: StoreProductResult) => string;
  exportFilenameBase: string;
  meta?: { totalStores?: number; inStockCount?: number; searchDurationMs?: number };
  t: TranslateFn;
}

export default function VirtualizedStoreSearchList({
  results,
  retailer,
  storeSort,
  onSortChange,
  storeFilter,
  onStoreFilterChange,
  inStockOnly,
  onInStockOnlyChange,
  getInventoryLabel,
  exportFilenameBase,
  meta,
  t,
}: VirtualizedStoreSearchListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    let rows = filterStoreSearchResults(results, storeFilter);
    if (inStockOnly) {
      rows = rows.filter(isStoreResultInStock);
    }
    return sortStoreSearchResults(rows, storeSort, retailer);
  }, [results, storeFilter, inStockOnly, storeSort, retailer]);

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 88,
    overscan: 8,
  });

  return (
    <div>
      {meta && (
        <div className="mx-3 mb-2 rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2 text-[11px] text-amber-900 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-100 sm:mx-4">
          Nationwide scan: {meta.totalStores ?? results.length} stores
          {meta.inStockCount != null ? ` · ${meta.inStockCount} in stock` : ""}
          {meta.searchDurationMs != null ? ` · ${(meta.searchDurationMs / 1000).toFixed(1)}s` : ""}
        </div>
      )}

      <div className="mx-3 flex flex-wrap items-center gap-2 px-1 pb-2 sm:mx-4">
        <div className="relative min-w-[180px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={storeFilter}
            onChange={(e) => onStoreFilterChange(e.target.value)}
            placeholder="Filter by store #, city, address..."
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-8 pr-3 text-xs text-gray-900 placeholder:text-gray-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:border-dark-600 dark:bg-dark-800 dark:text-white dark:placeholder-dark-400"
          />
        </div>
        <label className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-[11px] font-medium text-gray-700 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-100">
          <input
            type="checkbox"
            checked={inStockOnly}
            onChange={(e) => onInStockOnlyChange(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          In stock only
        </label>
        <button
          type="button"
          disabled={!filtered.length}
          onClick={() => downloadStoreSearchReport(filtered, "csv", exportFilenameBase)}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-[11px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-100"
        >
          <Download className="h-3.5 w-3.5" />
          CSV
        </button>
        <button
          type="button"
          disabled={!filtered.length}
          onClick={() => downloadStoreSearchReport(filtered, "json", exportFilenameBase)}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-[11px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-100"
        >
          <Download className="h-3.5 w-3.5" />
          JSON
        </button>
      </div>

      <div className="px-4 pb-1">
        <StoreSearchSortControls value={storeSort} onChange={onSortChange} />
      </div>

      <div className="flex items-center justify-end px-4 pb-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {filtered.length} {filtered.length === 1 ? "store" : "stores"}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="mx-3 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-3 py-8 text-center dark:border-dark-600 dark:bg-dark-800/50 sm:mx-4">
          <p className="text-xs font-medium text-gray-500 dark:text-dark-300">
            {t("products.outOfStock")}
          </p>
        </div>
      ) : (
        <div ref={listRef} className="mx-3 max-h-[min(60vh,520px)] overflow-auto sm:mx-4">
          <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
            {virtualizer.getVirtualItems().map((item) => {
              const result = filtered[item.index];
              return (
                <div
                  key={`${result.storeId}-${item.index}`}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${item.start}px)`,
                  }}
                  data-index={item.index}
                  ref={virtualizer.measureElement}
                  className="pb-1.5"
                >
                  <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 dark:border-dark-700 dark:bg-dark-800">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-bold text-gray-950 dark:text-white">#{result.storeId}</span>
                        {result.distance && (
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-dark-700 dark:text-dark-200">
                            {result.distance}
                          </span>
                        )}
                        {result.clearance && !result.possiblePenny && result.storePrice > 0 && result.storePrice < result.msrp && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            Clearance
                          </span>
                        )}
                        {retailer === "home_depot" && result.possiblePenny && <PossiblePennyBadge />}
                          {retailer === "home_depot" && (result.verificationStatus === "pending" || result.verificationStatus === "unverified") && (
                            <span className="text-[10px] text-gray-500 dark:text-dark-200">{t(result.verificationStatus === "pending" ? "products.pickupChecking" : "products.pickupUnverified")}</span>
                          )}
                        {!isStoreResultInStock(result) && (
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-dark-700">
                            OOS
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-[11px] text-gray-600 dark:text-dark-200" title={result.storeAddress}>
                        {result.storeAddress}
                      </p>
                      <div className="mt-1 text-[11px] text-gray-600 dark:text-dark-200">{getInventoryLabel(result)}</div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      {result.verificationStatus && !result.possiblePenny && !result.storePrice && <span className="text-xs text-gray-500">{t(result.verificationStatus === "pending" ? "products.priceChecking" : "products.priceUnverified")}</span>}
                      {(result.possiblePenny || !!result.storePrice) && (
                        <span className="text-lg font-bold leading-none text-gray-950 dark:text-white">
                          ${result.possiblePenny ? "0.01" : result.storePrice.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
